import { useState, useEffect, useMemo } from "react";
import { Helmet } from "react-helmet-async";
import { WebPageJsonLd } from "@/components/seo/WebPageJsonLd";
import { Link, useNavigate } from "react-router-dom";
import { mapGoalsToTags } from "@/lib/personalization";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";

import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, ArrowRight, Check, Sparkles, Activity, Zap, Heart, Brain, Scale, Dumbbell, ClipboardCheck, Stethoscope, Video, Truck, AlertTriangle } from "lucide-react";
import { NONE_OF_THE_ABOVE_OPTION, stripExclusiveNoneSelection, toggleExclusiveNoneSelection } from "@/lib/exclusive-none-option";
import { MEDICAL_SCREENING_CONDITIONS, hasGlp1ConditionDisqualifier } from "@/lib/glp1-screening";
import { US_STATES } from "@/lib/us-states";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { describeAuthError } from "@/lib/authErrors";
import { showLeakedPasswordToast } from "@/lib/authErrorToast";
import { useToast } from "@/hooks/use-toast";
import { useAuditLog } from "@/hooks/useAuditLog";
import Navbar from "@/components/Navbar";
import GoogleSignInButton from "@/components/GoogleSignInButton";
import AppleSignInButton from "@/components/AppleSignInButton";

type StepDef = {
  id: string;
  label: string;
  description: string;
};

const ALL_STEPS: StepDef[] = [
  { id: "goals", label: "Goals", description: "What brings you here?" },
  { id: "profile", label: "Profile", description: "Basic info" },
  { id: "body", label: "Body", description: "Metrics" },
  { id: "metabolic", label: "Metabolic", description: "Weight loss" },
  { id: "energy", label: "Energy", description: "Fatigue & focus" },
  { id: "recovery", label: "Recovery", description: "Performance" },
  { id: "hormonal", label: "Hormonal", description: "Longevity" },
  { id: "medical", label: "Medical", description: "Health screening" },
  { id: "lifestyle", label: "Lifestyle", description: "Daily habits" },
  { id: "timeline", label: "Timeline", description: "Readiness" },
  { id: "results", label: "Results", description: "Next steps" },
];

const GOAL_OPTIONS = [
  { label: "Lose weight", icon: Scale },
  { label: "Increase energy", icon: Zap },
  { label: "Improve recovery", icon: Dumbbell },
  { label: "Optimize long-term health", icon: Heart },
  { label: "Improve mental clarity", icon: Brain },
  { label: "Balance hormones", icon: Activity },
];

const GetStarted = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { logAccess } = useAuditLog();
  const [isLoading, setIsLoading] = useState(false);
  const [assessmentSubmitted, setAssessmentSubmitted] = useState(false);
  const [stateLicensed, setStateLicensed] = useState<boolean | null>(null);
  const [checkingState, setCheckingState] = useState(false);

  const isLoggedIn = !loading && !!user;
  const [currentStepIndex, setCurrentStepIndex] = useState(0);

  const [formData, setFormData] = useState({
    // Goals (multi-select)
    goals: [] as string[],
    // Profile
    ageRange: "",
    gender: "",
    state: "",
    // Body
    heightFeet: "",
    heightInches: "",
    weight: "",
    targetWeight: "",
    // Metabolic (conditional)
    weightToLose: "",
    struggledDespiteDiet: "",
    priorGLP1: "",
    // Energy (conditional)
    fatigueFrequency: "",
    sleepQuality: "",
    brainFog: "",
    // Recovery (conditional)
    painIndicators: "",
    exerciseFrequency: "",
    // Hormonal / Longevity
    hormonalSymptoms: [] as string[],
    recentBloodwork: "",
    // Medical screening
    conditions: [] as string[],
    takingMedications: "",
    medicationDetails: "",
    hasAllergies: "",
    allergyDetails: "",
    recentBariatricSurgery: "",
    pregnantOrTTC: "",
    substanceMisuseHistory: "",
    // Lifestyle
    activityLevel: "",
    eatingHabits: "",
    stressLevel: "",
    // Timeline
    startTimeline: "",
    // Account
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    phone: "",
    // Consents (PRD v3.0 — three explicit acknowledgments)
    consentStateAvailability: false,
    consentProviderRelationship: false,
    consentTelehealth: false,
  });

  // Compute visible steps based on selected goals
  const visibleSteps = useMemo(() => {
    return ALL_STEPS.filter((step) => {
      if (step.id === "metabolic") return formData.goals.includes("Lose weight");
      if (step.id === "energy") return formData.goals.includes("Increase energy");
      if (step.id === "recovery") return formData.goals.includes("Improve recovery");
      return true;
    });
  }, [formData.goals]);

  const currentStep = visibleSteps[currentStepIndex];
  const progress = ((currentStepIndex + 1) / visibleSteps.length) * 100;

  // If step disappears from under us, clamp
  useEffect(() => {
    if (currentStepIndex >= visibleSteps.length) {
      setCurrentStepIndex(visibleSteps.length - 1);
    }
  }, [visibleSteps.length, currentStepIndex]);

  // PRD v3.0 — state compliance engine: when patient picks a state, verify a licensed provider exists.
  useEffect(() => {
    if (!formData.state) {
      setStateLicensed(null);
      return;
    }
    let cancelled = false;
    setCheckingState(true);
    setStateLicensed(null);
    (async () => {
      const { data, error } = await supabase.functions.invoke("check-state-provider", { body: { state: formData.state } });
      if (cancelled) return;
      setCheckingState(false);
      setStateLicensed(error ? false : !!(data && (data as { licensed?: boolean }).licensed));
    })();
    return () => { cancelled = true; };
  }, [formData.state]);

  const updateField = (field: string, value: string | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const toggleGoal = (goal: string) => {
    setFormData((prev) => ({
      ...prev,
      goals: prev.goals.includes(goal)
        ? prev.goals.filter((g) => g !== goal)
        : [...prev.goals, goal],
    }));
  };

  const toggleArrayField = (field: "conditions" | "hormonalSymptoms", value: string) => {
    setFormData((prev) => {
      const arr = prev[field];
      if (value === "None") {
        return { ...prev, [field]: ["None"] };
      }
      const filtered = arr.filter((v) => v !== "None");
      return {
        ...prev,
        [field]: filtered.includes(value)
          ? filtered.filter((v) => v !== value)
          : [...filtered, value],
      };
    });
  };

  const toggleCondition = (value: string) => {
    setFormData((prev) => ({
      ...prev,
      conditions: toggleExclusiveNoneSelection(prev.conditions, value),
    }));
  };

  const allConsentsChecked =
    formData.consentStateAvailability && formData.consentProviderRelationship && formData.consentTelehealth;

  const canProceed = (): boolean => {
    if (!currentStep) return false;
    switch (currentStep.id) {
      case "goals": return formData.goals.length > 0;
      case "profile": return !!formData.ageRange && !!formData.gender && !!formData.state && stateLicensed === true;
      case "body": return !!formData.heightFeet && !!formData.heightInches && !!formData.weight;
      case "metabolic": return !!formData.weightToLose && !!formData.struggledDespiteDiet && !!formData.priorGLP1;
      case "energy": return !!formData.fatigueFrequency && !!formData.sleepQuality && !!formData.brainFog;
      case "recovery": return !!formData.painIndicators && !!formData.exerciseFrequency;
      case "hormonal": return formData.hormonalSymptoms.length > 0 && !!formData.recentBloodwork;
      case "medical": return formData.conditions.length > 0 && !!formData.takingMedications && !!formData.hasAllergies && !!formData.recentBariatricSurgery && !!formData.pregnantOrTTC && !!formData.substanceMisuseHistory;
      case "lifestyle": return !!formData.activityLevel && !!formData.eatingHabits && !!formData.stressLevel;
      case "timeline": return !!formData.startTimeline;
      case "results": return isLoggedIn ? allConsentsChecked : (!!formData.firstName && !!formData.lastName && !!formData.email && !!formData.password && allConsentsChecked);
      default: return true;
    }
  };

  const next = () => {
    if (!canProceed()) {
      toast({ title: "Please complete all fields", description: "Fill in the required selections before continuing.", variant: "destructive" });
      return;
    }
    setCurrentStepIndex((s) => Math.min(s + 1, visibleSteps.length - 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const back = () => {
    setCurrentStepIndex((s) => Math.max(s - 1, 0));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const buildAssessmentPayload = () => {
    const height = formData.heightFeet && formData.heightInches
      ? `${formData.heightFeet}'${formData.heightInches}"`
      : null;

    const conditions = stripExclusiveNoneSelection(formData.conditions).join(", ");
    const medications = formData.takingMedications === "Yes" ? formData.medicationDetails : "None";
    const allergies = formData.hasAllergies === "Yes" ? formData.allergyDetails : "None";

    const additionalInfo = [
      `Goals: ${formData.goals.join(", ")}`,
      `Age range: ${formData.ageRange}`,
      `Target weight: ${formData.targetWeight || "Not specified"}`,
      ...(formData.goals.includes("Lose weight") ? [
        `Weight to lose: ${formData.weightToLose}`,
        `Struggled despite diet/exercise: ${formData.struggledDespiteDiet}`,
        `Prior GLP-1 use: ${formData.priorGLP1}`,
      ] : []),
      ...(formData.goals.includes("Increase energy") ? [
        `Fatigue frequency: ${formData.fatigueFrequency}`,
        `Sleep quality: ${formData.sleepQuality}`,
        `Brain fog: ${formData.brainFog}`,
      ] : []),
      ...(formData.goals.includes("Improve recovery") ? [
        `Pain/injury indicators: ${formData.painIndicators}`,
        `Exercise frequency: ${formData.exerciseFrequency}`,
      ] : []),
      `Hormonal symptoms: ${formData.hormonalSymptoms.join(", ") || "None"}`,
      `Recent bloodwork: ${formData.recentBloodwork}`,
      `Recent bariatric surgery: ${formData.recentBariatricSurgery}`,
      `Pregnant/breastfeeding/TTC: ${formData.pregnantOrTTC}`,
      `Substance misuse history: ${formData.substanceMisuseHistory}`,
      `Activity level: ${formData.activityLevel}`,
      `Eating habits: ${formData.eatingHabits}`,
      `Stress level: ${formData.stressLevel}`,
      `Timeline: ${formData.startTimeline}`,
    ].join("; ");

    const profile: {
      sex?: string | null;
      height?: string | null;
      weight?: number | null;
      first_name?: string | null;
      last_name?: string | null;
      phone?: string | null;
      state?: string | null;
      state_acknowledged_at?: string;
      provider_relationship_acknowledged_at?: string;
      telehealth_consent_at?: string;
    } = {
      sex: formData.gender ? formData.gender.toLowerCase() : null,
      height,
      weight: formData.weight ? Number(formData.weight) : null,
    };

    // Only include name/phone when the user filled them in (new account flow)
    if (formData.firstName) profile.first_name = formData.firstName;
    if (formData.lastName) profile.last_name = formData.lastName;
    if (formData.phone) profile.phone = formData.phone;
    if (formData.state) profile.state = formData.state;

    // Stamp consents (PRD v3.0)
    const now = new Date().toISOString();
    if (formData.consentStateAvailability) profile.state_acknowledged_at = now;
    if (formData.consentProviderRelationship) profile.provider_relationship_acknowledged_at = now;
    if (formData.consentTelehealth) profile.telehealth_consent_at = now;

    return {
      goal: formData.goals.join(", "),
      conditions: conditions || null,
      medications: medications || null,
      allergies: allergies || null,
      additional_info: additionalInfo,
      profile,
    };
  };

  const logConsents = async (forUserId: string) => {
    try {
      await Promise.all([
        logAccess({ action: "record_consent", resource_type: "consent", metadata: { type: "state_availability", state: formData.state, user_id: forUserId } }),
        logAccess({ action: "record_consent", resource_type: "consent", metadata: { type: "provider_relationship", user_id: forUserId } }),
        logAccess({ action: "record_consent", resource_type: "consent", metadata: { type: "telehealth", user_id: forUserId } }),
      ]);
    } catch (err) {
      console.error("Failed to log consents:", err);
    }
  };

  const handleCreateAccountAndSubmit = async () => {
    if (!canProceed()) {
      toast({ title: "Please complete all fields", description: "Fill in all required fields before continuing.", variant: "destructive" });
      return;
    }

    setIsLoading(true);

    // Use supabase.auth.signUp directly to get the full response (user + session)
    const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
      email: formData.email,
      password: formData.password,
      options: { emailRedirectTo: `${window.location.origin}/dashboard` },
    });

    if (signUpError) {
      const friendly = describeAuthError(signUpError, { context: "signup" });
      if (friendly.isLeakedPassword) {
        showLeakedPasswordToast(toast, friendly, { navigate });
      } else {
        toast({ title: friendly.title, description: friendly.description, variant: "destructive" });
      }
      setIsLoading(false);
      return;
    }

    const payload = buildAssessmentPayload();
    const newUserId = signUpData.user?.id;

    if (!newUserId) {
      toast({ title: "Error", description: "Could not create your account. Please try again.", variant: "destructive" });
      setIsLoading(false);
      return;
    }

    if (signUpData.session) {
      // Auto-confirmed — user is signed in, save directly
      const { error: newProfileErr } = await supabase.from("profiles").update(payload.profile).eq("user_id", newUserId);
      if (newProfileErr) {
        console.error("Profile update failed for new user:", newProfileErr);
      }

      // Check for an existing pending assessment (edge case: user signed up before and has one)
      const { data: existingNew } = await supabase
        .from("health_assessments")
        .select("id")
        .eq("user_id", newUserId)
        .eq("status", "pending")
        .order("created_at", { ascending: false })
        .limit(1);

      if (existingNew && existingNew.length > 0) {
        await supabase.from("health_assessments").update({
          goal: payload.goal,
          conditions: payload.conditions,
          medications: payload.medications,
          allergies: payload.allergies,
          additional_info: payload.additional_info,
          updated_at: new Date().toISOString(),
        }).eq("id", existingNew[0].id);
      } else {
        await supabase.from("health_assessments").insert({
          user_id: newUserId,
          goal: payload.goal,
          conditions: payload.conditions,
          medications: payload.medications,
          allergies: payload.allergies,
          additional_info: payload.additional_info,
        });
      }

      await logConsents(newUserId);
      toast({ title: "Assessment submitted!", description: "A provider will review your information shortly." });
      setIsLoading(false);
      setAssessmentSubmitted(true);
    } else {
      // Email confirmation required — save via edge function (service role)
      const { data: saveData, error: saveError } = await supabase.functions.invoke("save-assessment", {
        body: { user_id: newUserId, ...payload },
      });

      if (saveError) {
        console.error("Failed to save assessment for unconfirmed user:", saveError);
        toast({
          title: "Account created, but assessment save failed",
          description: "Please log in after verifying your email and resubmit your assessment.",
          variant: "destructive",
        });
      } else {
        toast({
          title: "Account created!",
          description: "Please check your email to verify your account, then log in to continue.",
        });
      }

      setIsLoading(false);
      navigate("/login");
    }
  };

  const handleSubmit = async () => {
    if (!allConsentsChecked) {
      toast({ title: "Consent required", description: "Please confirm all three acknowledgments before submitting.", variant: "destructive" });
      return;
    }
    setIsLoading(true);

    const { data: { user: currentUser } } = await supabase.auth.getUser();
    if (!currentUser) {
      toast({ title: "Error", description: "Please sign in first.", variant: "destructive" });
      setIsLoading(false);
      return;
    }

    const payload = buildAssessmentPayload();

    const { error: profileError } = await supabase.from("profiles").update(payload.profile).eq("user_id", currentUser.id);
    if (profileError) {
      console.error("Profile update failed:", profileError);
      toast({ title: "Error updating profile", description: profileError.message, variant: "destructive" });
      setIsLoading(false);
      return;
    }

    // Check for an existing pending assessment to update instead of creating duplicates
    const { data: existing } = await supabase
      .from("health_assessments")
      .select("id")
      .eq("user_id", currentUser.id)
      .eq("status", "pending")
      .order("created_at", { ascending: false })
      .limit(1);

    if (existing && existing.length > 0) {
      await supabase.from("health_assessments").update({
        goal: payload.goal,
        conditions: payload.conditions,
        medications: payload.medications,
        allergies: payload.allergies,
        additional_info: payload.additional_info,
        updated_at: new Date().toISOString(),
      }).eq("id", existing[0].id);
      toast({ title: "Assessment updated!", description: "Your updated information will be reviewed by a provider." });
    } else {
      await supabase.from("health_assessments").insert({
        user_id: currentUser.id,
        goal: payload.goal,
        conditions: payload.conditions,
        medications: payload.medications,
        allergies: payload.allergies,
        additional_info: payload.additional_info,
      });
      toast({ title: "Assessment submitted!", description: "A provider will review your information shortly." });
    }

    await logConsents(currentUser.id);
    setIsLoading(false);
    setAssessmentSubmitted(true);
  };

  // Determine treatment categories for results
  const treatmentCategories = useMemo(() => {
    const cats: { label: string; description: string }[] = [];
    if (formData.goals.includes("Lose weight")) cats.push({ label: "Metabolic Support", description: "Treatments designed to support sustainable weight management" });
    if (formData.goals.includes("Increase energy")) cats.push({ label: "Energy Optimization", description: "Therapies that may help improve energy and reduce fatigue" });
    if (formData.goals.includes("Improve recovery")) cats.push({ label: "Recovery Support", description: "Approaches designed to support physical recovery and performance" });
    if (formData.goals.includes("Optimize long-term health") || formData.goals.includes("Balance hormones")) cats.push({ label: "Hormonal & Longevity Optimization", description: "Personalized strategies for long-term health and hormonal balance" });
    if (formData.goals.includes("Improve mental clarity")) cats.push({ label: "Cognitive Support", description: "Options that may support mental clarity and focus" });
    if (cats.length === 0) cats.push({ label: "General Wellness", description: "A comprehensive evaluation of your health goals" });
    return cats;
  }, [formData.goals]);

  const OptionCard = ({ selected, onClick, children }: { selected: boolean; onClick: () => void; children: React.ReactNode }) => (
    <button
      type="button"
      onClick={onClick}
      className={`w-full text-left p-4 rounded-xl border transition-colors ${
        selected
          ? "border-primary bg-primary/5 text-foreground"
          : "border-border hover:border-primary/30 text-muted-foreground"
      }`}
    >
      {children}
    </button>
  );

  const MultiSelectCard = ({ selected, onClick, children }: { selected: boolean; onClick: () => void; children: React.ReactNode }) => (
    <button
      type="button"
      onClick={onClick}
      className={`w-full text-left p-4 rounded-xl border transition-colors flex items-center gap-3 ${
        selected
          ? "border-primary bg-primary/5 text-foreground"
          : "border-border hover:border-primary/30 text-muted-foreground"
      }`}
    >
      <div className={`w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 transition-colors ${selected ? "border-primary bg-primary" : "border-muted-foreground/40"}`}>
        {selected && <Check size={12} className="text-primary-foreground" />}
      </div>
      <div className="flex-1">{children}</div>
    </button>
  );

  if (assessmentSubmitted) {
    const tags = mapGoalsToTags(formData.goals);
    const customizeUrl = `/customize?tags=${tags.join(",")}`;

    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="container mx-auto px-4 pt-24 pb-16 max-w-xl">
          <Card className="border-border bg-card overflow-hidden">
            {/* Success header */}
            <div className="bg-primary/5 border-b border-primary/10 px-6 py-8 text-center">
              <div className="w-16 h-16 mx-auto rounded-full bg-primary/10 flex items-center justify-center mb-4">
                <ClipboardCheck size={28} className="text-primary" />
              </div>
              <h1 className="font-display text-2xl text-foreground mb-2">Assessment Submitted!</h1>
              <p className="text-muted-foreground text-sm max-w-sm mx-auto">
                Your health profile is now in our system. Here's what happens from here.
              </p>
            </div>

            <CardContent className="p-6 space-y-6">
              {/* Journey timeline */}
              <div className="space-y-1">
                <h3 className="font-display text-lg text-foreground mb-4">Your journey to treatment</h3>

                <div className="relative space-y-0">
                  {/* Step 1 — completed */}
                  <div className="flex gap-4 pb-6 relative">
                    <div className="flex flex-col items-center">
                      <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center shrink-0">
                        <Check size={16} className="text-primary-foreground" />
                      </div>
                      <div className="w-0.5 flex-1 bg-primary/30 mt-1" />
                    </div>
                    <div className="pt-1">
                      <p className="font-medium text-foreground text-sm">Health assessment completed</p>
                      <p className="text-xs text-muted-foreground mt-0.5">You just finished this step. Nice work!</p>
                    </div>
                  </div>

                  {/* Step 2 — current/next */}
                  <div className="flex gap-4 pb-6 relative">
                    <div className="flex flex-col items-center">
                      <div className="w-8 h-8 rounded-full border-2 border-primary bg-primary/10 flex items-center justify-center shrink-0 animate-pulse">
                        <Sparkles size={16} className="text-primary" />
                      </div>
                      <div className="w-0.5 flex-1 bg-border mt-1" />
                    </div>
                    <div className="pt-1">
                      <p className="font-medium text-foreground text-sm">Choose your plan</p>
                      <p className="text-xs text-muted-foreground mt-0.5">Select add-ons that match your goals. No commitment until checkout.</p>
                    </div>
                  </div>

                  {/* Step 3 — upcoming */}
                  <div className="flex gap-4 pb-6 relative">
                    <div className="flex flex-col items-center">
                      <div className="w-8 h-8 rounded-full border-2 border-border bg-muted flex items-center justify-center shrink-0">
                        <Stethoscope size={14} className="text-muted-foreground" />
                      </div>
                      <div className="w-0.5 flex-1 bg-border mt-1" />
                    </div>
                    <div className="pt-1">
                      <p className="font-medium text-muted-foreground text-sm">Provider review</p>
                      <p className="text-xs text-muted-foreground mt-0.5">A licensed provider reviews your profile, typically within 24 to 48 hours.</p>
                    </div>
                  </div>

                  {/* Step 4 */}
                  <div className="flex gap-4 pb-6 relative">
                    <div className="flex flex-col items-center">
                      <div className="w-8 h-8 rounded-full border-2 border-border bg-muted flex items-center justify-center shrink-0">
                        <Video size={14} className="text-muted-foreground" />
                      </div>
                      <div className="w-0.5 flex-1 bg-border mt-1" />
                    </div>
                    <div className="pt-1">
                      <p className="font-medium text-muted-foreground text-sm">Video or audio consultation</p>
                      <p className="text-xs text-muted-foreground mt-0.5">Discuss your treatment plan one-on-one with your provider.</p>
                    </div>
                  </div>

                  {/* Step 5 */}
                  <div className="flex gap-4 relative">
                    <div className="flex flex-col items-center">
                      <div className="w-8 h-8 rounded-full border-2 border-border bg-muted flex items-center justify-center shrink-0">
                        <Truck size={14} className="text-muted-foreground" />
                      </div>
                    </div>
                    <div className="pt-1">
                      <p className="font-medium text-muted-foreground text-sm">Treatment ships</p>
                      <p className="text-xs text-muted-foreground mt-0.5">If approved, your prescription ships from a licensed pharmacy.</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Reassurance */}
              <div className="p-4 rounded-xl bg-muted/50 border border-border">
                <p className="text-sm text-muted-foreground">
                  <span className="font-medium text-foreground">No obligation yet.</span> Choosing a plan doesn't commit you to anything. Your provider will review everything before treatment begins.
                </p>
              </div>

              {/* CTA */}
              <Button onClick={() => navigate(customizeUrl)} className="w-full gap-2" size="lg">
                Choose Your Plan <ArrowRight size={16} />
              </Button>

              <p className="text-center">
                <Link to="/dashboard" className="text-xs text-muted-foreground hover:text-foreground transition-colors underline">
                  or go to your dashboard
                </Link>
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (!currentStep) return null;

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Get Started | Personalized Peptide Therapy | Elara Health</title>
        <meta name="description" content="Get your personalized peptide therapy plan in 2 minutes. Clinician-guided metabolic and longevity care from Elara Health." />
        <link rel="canonical" href="https://elarahealth.life/get-started" />
        <meta property="og:title" content="Get Started | Elara Health" />
        <meta property="og:url" content="https://elarahealth.life/get-started" />
      </Helmet>
    <WebPageJsonLd
      path="/get-started"
      name={"Get Started with Elara Health"}
      description={"Get your personalized peptide therapy plan in 2 minutes. Clinician-guided metabolic and longevity care from Elara Health."}
    />
      <Navbar />
      <div className="container mx-auto px-4 pt-24 pb-16 max-w-xl">
        <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-6">
          <ArrowLeft size={16} /> Back to home
        </Link>

        <h1 className="font-display text-3xl text-foreground mb-2">Get Your Personalized Health Plan in 2 Minutes</h1>
        <p className="text-muted-foreground mb-1">Answer a few quick questions to see what treatment options a provider may evaluate you for.</p>
        <p className="text-xs text-muted-foreground/80 mb-6">Private and secure · No obligation</p>

        {/* Progress bar */}
        <div className="w-full h-2 bg-muted rounded-full mb-8 overflow-hidden">
          <div className="h-full bg-primary rounded-full transition-all duration-500" style={{ width: `${progress}%` }} />
        </div>

        <Card className="border-border bg-card">
          <CardContent className="p-6 md:p-8">

            {/* GOALS — multi-select */}
            {currentStep.id === "goals" && (
              <div className="space-y-6">
                <div>
                  <h2 className="font-display text-xl text-foreground mb-1">What are your primary health goals?</h2>
                  <p className="text-sm text-muted-foreground">Select all that apply.</p>
                </div>
                <div className="space-y-3">
                  {GOAL_OPTIONS.map(({ label, icon: Icon }) => (
                    <MultiSelectCard key={label} selected={formData.goals.includes(label)} onClick={() => toggleGoal(label)}>
                      <div className="flex items-center gap-2">
                        <Icon size={16} className="shrink-0" />
                        <span>{label}</span>
                      </div>
                    </MultiSelectCard>
                  ))}
                </div>
              </div>
            )}

            {/* PROFILE */}
            {currentStep.id === "profile" && (
              <div className="space-y-6">
                <h2 className="font-display text-xl text-foreground">Tell us about yourself</h2>
                <div>
                  <Label className="mb-2 block">Age range</Label>
                  <div className="grid grid-cols-3 gap-2 sm:grid-cols-5">
                    {["Under 25", "25 to 34", "35 to 44", "45 to 54", "55+"].map((a) => (
                      <button
                        key={a}
                        type="button"
                        onClick={() => updateField("ageRange", a)}
                        className={`p-3 rounded-xl border text-center text-sm transition-colors ${
                          formData.ageRange === a
                            ? "border-primary bg-primary/5 text-foreground font-medium"
                            : "border-border hover:border-primary/30 text-muted-foreground"
                        }`}
                      >
                        {a}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <Label className="mb-2 block">Gender</Label>
                  <div className="grid grid-cols-3 gap-2">
                    {["Male", "Female", "Prefer not to say"].map((g) => (
                      <button
                        key={g}
                        type="button"
                        onClick={() => updateField("gender", g)}
                        className={`p-3 rounded-xl border text-center text-sm transition-colors ${
                          formData.gender === g
                            ? "border-primary bg-primary/5 text-foreground font-medium"
                            : "border-border hover:border-primary/30 text-muted-foreground"
                        }`}
                      >
                        {g}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <Label className="mb-2 block">State of residence</Label>
                  <Select value={formData.state} onValueChange={(v) => updateField("state", v)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select your state" />
                    </SelectTrigger>
                    <SelectContent className="max-h-72">
                      {US_STATES.map((s) => (
                        <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground mt-2">
                    We can only treat patients in states where our physicians are licensed. We check this in real time.
                  </p>
                  {formData.state && checkingState && (
                    <p className="text-xs text-muted-foreground mt-2 italic">Checking provider availability in your state…</p>
                  )}
                  {formData.state && !checkingState && stateLicensed === false && (
                    <div className="mt-3 flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2.5 text-xs">
                      <AlertTriangle size={14} className="mt-0.5 flex-shrink-0 text-destructive" />
                      <p className="text-foreground leading-relaxed">
                        <span className="font-semibold text-destructive">We're not yet licensed in {US_STATES.find((s) => s.value === formData.state)?.label}.</span>{" "}
                        We're expanding coverage — leave us your email on the home page and we'll notify you when care opens up in your state.
                      </p>
                    </div>
                  )}
                  {formData.state && !checkingState && stateLicensed === true && (
                    <p className="text-xs text-primary mt-2 inline-flex items-center gap-1">
                      <Check size={12} /> Care available in {US_STATES.find((s) => s.value === formData.state)?.label}
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* BODY METRICS */}
            {currentStep.id === "body" && (
              <div className="space-y-6">
                <h2 className="font-display text-xl text-foreground">Body metrics</h2>
                <div className="space-y-2">
                  <Label>Height</Label>
                  <div className="flex gap-2">
                    <Select value={formData.heightFeet} onValueChange={(v) => updateField("heightFeet", v)}>
                      <SelectTrigger className="flex-1"><SelectValue placeholder="Feet" /></SelectTrigger>
                      <SelectContent>
                        {[3, 4, 5, 6, 7].map((ft) => (
                          <SelectItem key={ft} value={String(ft)}>{ft} ft</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Select value={formData.heightInches} onValueChange={(v) => updateField("heightInches", v)}>
                      <SelectTrigger className="flex-1"><SelectValue placeholder="Inches" /></SelectTrigger>
                      <SelectContent>
                        {Array.from({ length: 12 }, (_, i) => (
                          <SelectItem key={i} value={String(i)}>{i} in</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="weight">Current weight (lbs)</Label>
                  <Input id="weight" type="number" value={formData.weight} onChange={(e) => updateField("weight", e.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="targetWeight">Target weight (optional)</Label>
                  <Input id="targetWeight" type="number" placeholder="Optional" value={formData.targetWeight} onChange={(e) => updateField("targetWeight", e.target.value)} />
                </div>
              </div>
            )}

            {/* METABOLIC — conditional on "Lose weight" */}
            {currentStep.id === "metabolic" && (() => {
              const heightIn = (Number(formData.heightFeet) * 12) + Number(formData.heightInches);
              const weightLbs = Number(formData.weight);
              const bmi = heightIn > 0 && weightLbs > 0 ? (weightLbs / (heightIn * heightIn)) * 703 : null;
              const showBmiNote = bmi !== null && bmi < 25 && formData.goals.includes("Lose weight");
              return (
              <div className="space-y-6">
                <h2 className="font-display text-xl text-foreground">Weight loss details</h2>
                {showBmiNote && (
                  <p className="text-sm text-muted-foreground italic p-3 rounded-lg bg-muted/50 border border-border">
                    Based on your metrics, GLP-1 therapy may not be the best fit, but your provider will review all options with you.
                  </p>
                )}
                <div>
                  <Label className="mb-2 block">How much weight would you like to lose?</Label>
                  <div className="space-y-3">
                    {["10 to 20 lbs", "20 to 40 lbs", "40 to 60 lbs", "60+ lbs"].map((v) => (
                      <OptionCard key={v} selected={formData.weightToLose === v} onClick={() => updateField("weightToLose", v)}>{v}</OptionCard>
                    ))}
                  </div>
                </div>
                <div>
                  <Label className="mb-2 block">Have you struggled to lose weight despite diet and exercise?</Label>
                  <div className="space-y-3">
                    {["Yes", "No"].map((v) => (
                      <OptionCard key={v} selected={formData.struggledDespiteDiet === v} onClick={() => updateField("struggledDespiteDiet", v)}>{v}</OptionCard>
                    ))}
                  </div>
                </div>
                <div>
                  <Label className="mb-2 block">Have you used GLP-1 medications before?</Label>
                  <div className="space-y-3">
                    {["Yes", "No", "Not sure"].map((v) => (
                      <OptionCard key={v} selected={formData.priorGLP1 === v} onClick={() => updateField("priorGLP1", v)}>{v}</OptionCard>
                    ))}
                  </div>
                </div>
              </div>
              );
            })()}

            {/* ENERGY — conditional on "Increase energy" */}
            {currentStep.id === "energy" && (
              <div className="space-y-6">
                <h2 className="font-display text-xl text-foreground">Energy & fatigue</h2>
                <div>
                  <Label className="mb-2 block">How often do you experience fatigue?</Label>
                  <div className="space-y-3">
                    {["Daily", "Several times a week", "Occasionally", "Rarely"].map((v) => (
                      <OptionCard key={v} selected={formData.fatigueFrequency === v} onClick={() => updateField("fatigueFrequency", v)}>{v}</OptionCard>
                    ))}
                  </div>
                </div>
                <div>
                  <Label className="mb-2 block">How would you rate your sleep quality?</Label>
                  <div className="space-y-3">
                    {["Poor", "Fair", "Good", "Excellent"].map((v) => (
                      <OptionCard key={v} selected={formData.sleepQuality === v} onClick={() => updateField("sleepQuality", v)}>{v}</OptionCard>
                    ))}
                  </div>
                </div>
                <div>
                  <Label className="mb-2 block">Do you experience brain fog or difficulty concentrating?</Label>
                  <div className="space-y-3">
                    {["Yes, frequently", "Sometimes", "Rarely", "Never"].map((v) => (
                      <OptionCard key={v} selected={formData.brainFog === v} onClick={() => updateField("brainFog", v)}>{v}</OptionCard>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* RECOVERY — conditional on "Improve recovery" */}
            {currentStep.id === "recovery" && (
              <div className="space-y-6">
                <h2 className="font-display text-xl text-foreground">Recovery & performance</h2>
                <div>
                  <Label className="mb-2 block">Do you experience chronic pain or injury-related discomfort?</Label>
                  <div className="space-y-3">
                    {["Yes, frequently", "Occasionally", "Rarely", "No"].map((v) => (
                      <OptionCard key={v} selected={formData.painIndicators === v} onClick={() => updateField("painIndicators", v)}>{v}</OptionCard>
                    ))}
                  </div>
                </div>
                <div>
                  <Label className="mb-2 block">How often do you exercise?</Label>
                  <div className="space-y-3">
                    {["Daily", "3 to 5 times per week", "1 to 2 times per week", "Rarely"].map((v) => (
                      <OptionCard key={v} selected={formData.exerciseFrequency === v} onClick={() => updateField("exerciseFrequency", v)}>{v}</OptionCard>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* HORMONAL / LONGEVITY — always shown */}
            {currentStep.id === "hormonal" && (
              <div className="space-y-6">
                <h2 className="font-display text-xl text-foreground mb-4">Hormonal & longevity</h2>
                <div>
                  <Label className="mb-2 block">Have you experienced any of the following? (select all that apply)</Label>
                  <div className="space-y-3">
                    {["Low energy", "Low libido", "Mood changes", "Unexplained weight gain", "None of the above"].map((s) => (
                      <MultiSelectCard
                        key={s}
                        selected={formData.hormonalSymptoms.includes(s === "None of the above" ? "None" : s)}
                        onClick={() => toggleArrayField("hormonalSymptoms", s === "None of the above" ? "None" : s)}
                      >
                        {s}
                      </MultiSelectCard>
                    ))}
                  </div>
                </div>
                <div>
                  <Label className="mb-2 block">Have you had bloodwork done in the past 12 months?</Label>
                  <div className="space-y-3">
                    {["Yes", "No"].map((v) => (
                      <OptionCard key={v} selected={formData.recentBloodwork === v} onClick={() => updateField("recentBloodwork", v)}>{v}</OptionCard>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* MEDICAL SCREENING */}
            {currentStep.id === "medical" && (() => {
              const hasDisqualifier = hasGlp1ConditionDisqualifier(formData.conditions)
                || formData.recentBariatricSurgery === "Yes"
                || formData.pregnantOrTTC === "Yes"
                || formData.substanceMisuseHistory === "Yes";
              const isNoneConditionSelected = formData.conditions.includes(NONE_OF_THE_ABOVE_OPTION) || formData.conditions.includes("None");
              return (
              <div className="space-y-6">
                <div>
                  <h2 className="font-display text-xl text-foreground mb-1">Medical screening</h2>
                  <p className="text-sm text-muted-foreground">Have you been diagnosed with any of the following?</p>
                  <div className="space-y-3">
                    {MEDICAL_SCREENING_CONDITIONS.map((c) => (
                      <MultiSelectCard
                        key={c}
                        selected={c === NONE_OF_THE_ABOVE_OPTION ? isNoneConditionSelected : formData.conditions.includes(c)}
                        onClick={() => toggleCondition(c)}
                      >
                        {c}
                      </MultiSelectCard>
                    ))}
                  </div>
                </div>

                <div>
                  <Label className="mb-2 block">Have you had bariatric surgery in the past 12 months?</Label>
                  <div className="space-y-3">
                    {["Yes", "No"].map((v) => (
                      <OptionCard key={v} selected={formData.recentBariatricSurgery === v} onClick={() => updateField("recentBariatricSurgery", v)}>{v}</OptionCard>
                    ))}
                  </div>
                </div>

                <div>
                  <Label className="mb-2 block">Are you currently pregnant, breastfeeding, or trying to conceive?</Label>
                  <div className="space-y-3">
                    {["Yes", "No"].map((v) => (
                      <OptionCard key={v} selected={formData.pregnantOrTTC === v} onClick={() => updateField("pregnantOrTTC", v)}>{v}</OptionCard>
                    ))}
                  </div>
                </div>

                <div>
                  <Label className="mb-2 block">Do you have a history of substance misuse?</Label>
                  <div className="space-y-3">
                    {["Yes", "No"].map((v) => (
                      <OptionCard key={v} selected={formData.substanceMisuseHistory === v} onClick={() => updateField("substanceMisuseHistory", v)}>{v}</OptionCard>
                    ))}
                  </div>
                </div>

                <div>
                  <Label className="mb-2 block">Are you currently taking any medications?</Label>
                  <div className="space-y-3">
                    {["Yes", "No"].map((v) => (
                      <OptionCard key={v} selected={formData.takingMedications === v} onClick={() => updateField("takingMedications", v)}>{v}</OptionCard>
                    ))}
                  </div>
                  {formData.takingMedications === "Yes" && (
                    <Input className="mt-3" placeholder="Please list your medications" value={formData.medicationDetails} onChange={(e) => updateField("medicationDetails", e.target.value)} />
                  )}
                </div>

                <div>
                  <Label className="mb-2 block">Do you have any known allergies?</Label>
                  <div className="space-y-3">
                    {["Yes", "No"].map((v) => (
                      <OptionCard key={v} selected={formData.hasAllergies === v} onClick={() => updateField("hasAllergies", v)}>{v}</OptionCard>
                    ))}
                  </div>
                  {formData.hasAllergies === "Yes" && (
                    <Input className="mt-3" placeholder="Please list your allergies (e.g., penicillin, latex, shellfish)" value={formData.allergyDetails} onChange={(e) => updateField("allergyDetails", e.target.value)} />
                  )}
                </div>

                {hasDisqualifier && (
                  <p className="text-sm text-muted-foreground italic p-3 rounded-lg bg-muted/50 border border-border">
                    Some of your responses may affect eligibility for certain therapies. Your provider will discuss the best options for you during your consultation.
                  </p>
                )}
              </div>
              );
            })()}

            {/* LIFESTYLE */}
            {currentStep.id === "lifestyle" && (
              <div className="space-y-6">
                <h2 className="font-display text-xl text-foreground">Your lifestyle</h2>
                <div>
                  <Label className="mb-2 block">Activity level</Label>
                  <div className="space-y-3">
                    {["Low", "Moderate", "High"].map((v) => (
                      <OptionCard key={v} selected={formData.activityLevel === v} onClick={() => updateField("activityLevel", v)}>{v}</OptionCard>
                    ))}
                  </div>
                </div>
                <div>
                  <Label className="mb-2 block">Eating habits</Label>
                  <div className="space-y-3">
                    {["Balanced", "Needs improvement", "Not sure"].map((v) => (
                      <OptionCard key={v} selected={formData.eatingHabits === v} onClick={() => updateField("eatingHabits", v)}>{v}</OptionCard>
                    ))}
                  </div>
                </div>
                <div>
                  <Label className="mb-2 block">Stress level</Label>
                  <div className="space-y-3">
                    {["Low", "Moderate", "High", "Very high"].map((v) => (
                      <OptionCard key={v} selected={formData.stressLevel === v} onClick={() => updateField("stressLevel", v)}>{v}</OptionCard>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* TIMELINE */}
            {currentStep.id === "timeline" && (
              <div className="space-y-6">
                <div>
                  <h2 className="font-display text-xl text-foreground mb-1">How quickly are you looking to start?</h2>
                  <p className="text-sm text-muted-foreground">This helps us prioritize your review.</p>
                </div>
                <div className="space-y-3">
                  {["Immediately", "Within a month", "Just exploring"].map((v) => (
                    <OptionCard key={v} selected={formData.startTimeline === v} onClick={() => updateField("startTimeline", v)}>{v}</OptionCard>
                  ))}
                </div>
              </div>
            )}

            {/* ACCOUNT — removed as standalone step, now gated at results */}

            {/* RESULTS */}
            {currentStep.id === "results" && (
              <div className="space-y-6">
                <div className="text-center">
                  <div className="w-16 h-16 mx-auto rounded-full bg-primary/10 flex items-center justify-center mb-4">
                    <Sparkles size={28} className="text-primary" />
                  </div>
                  <h2 className="font-display text-2xl text-foreground mb-2">Your Personalized Health Plan</h2>
                  <p className="text-muted-foreground">
                    Based on your responses, a provider may evaluate you for:
                  </p>
                </div>

                <div className="space-y-3">
                  {treatmentCategories.map((cat) => (
                    <div key={cat.label} className="p-4 rounded-xl border border-primary/20 bg-primary/5">
                      <p className="font-semibold text-foreground">{cat.label}</p>
                      <p className="text-sm text-muted-foreground mt-1">{cat.description}</p>
                    </div>
                  ))}
                </div>

                <div className="p-4 rounded-xl bg-muted/50 border border-border">
                  <p className="text-sm text-muted-foreground italic">
                    Patients with similar profiles are often evaluated for combination approaches designed to support weight, energy, and recovery.
                  </p>
                </div>

                {/* What Happens Next */}
                <div className="space-y-3 pt-2">
                  <h3 className="font-display text-lg text-foreground">What happens next?</h3>
                  <ol className="space-y-3 text-sm">
                    <li className="flex gap-3">
                      <span className="flex items-center justify-center w-6 h-6 rounded-full bg-primary/10 text-primary text-xs font-bold shrink-0">1</span>
                      <span className="text-muted-foreground"><span className="font-medium text-foreground">Choose your plan.</span> Pick add-ons that match your goals. No commitment until checkout.</span>
                    </li>
                    <li className="flex gap-3">
                      <span className="flex items-center justify-center w-6 h-6 rounded-full bg-primary/10 text-primary text-xs font-bold shrink-0">2</span>
                      <span className="text-muted-foreground"><span className="font-medium text-foreground">Provider review.</span> A licensed provider reviews your health profile (typically within 24 to 48 hours).</span>
                    </li>
                    <li className="flex gap-3">
                      <span className="flex items-center justify-center w-6 h-6 rounded-full bg-primary/10 text-primary text-xs font-bold shrink-0">3</span>
                      <span className="text-muted-foreground"><span className="font-medium text-foreground">Secure messaging.</span> Discuss your treatment plan with your provider by secure message. No video calls.</span>
                    </li>
                    <li className="flex gap-3">
                      <span className="flex items-center justify-center w-6 h-6 rounded-full bg-primary/10 text-primary text-xs font-bold shrink-0">4</span>
                      <span className="text-muted-foreground"><span className="font-medium text-foreground">Treatment begins.</span> If approved, your prescription ships from a licensed pharmacy.</span>
                    </li>
                  </ol>
                </div>

                {/* If not logged in, show account creation form */}
                {!isLoggedIn && (
                  <div className="space-y-4 pt-4 border-t border-border">
                    <h3 className="font-display text-lg text-foreground">Create an account to submit your assessment</h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="firstName">First name</Label>
                        <Input id="firstName" value={formData.firstName} onChange={(e) => updateField("firstName", e.target.value)} />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="lastName">Last name</Label>
                        <Input id="lastName" value={formData.lastName} onChange={(e) => updateField("lastName", e.target.value)} />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email">Email</Label>
                      <Input id="email" type="email" value={formData.email} onChange={(e) => updateField("email", e.target.value)} />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="password">Password</Label>
                      <Input id="password" type="password" value={formData.password} onChange={(e) => updateField("password", e.target.value)} />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="phone">Phone number (optional)</Label>
                      <Input id="phone" type="tel" value={formData.phone} onChange={(e) => updateField("phone", e.target.value)} />
                    </div>
                    <GoogleSignInButton label="Sign up with Google" />
                    <AppleSignInButton label="Sign up with Apple" />
                  </div>
                )}

                {/* PRD §7.1 — explicit provider-review acknowledgment */}
                <div className="rounded-lg border border-primary/20 bg-primary/5 p-4 text-xs text-muted-foreground leading-relaxed space-y-2">
                  <p className="font-medium text-foreground">A licensed physician will review your responses.</p>
                  <p>
                    Elara Health is a technology and administrative platform. Clinical care is delivered by independently licensed physicians who exercise their own clinical judgment. Treatment is not guaranteed, and additional consultation (including a live video visit) may be required.
                  </p>
                </div>

                {/* PRD v3.0 — three explicit acknowledgments */}
                <div className="space-y-3 pt-2">
                  <div className="flex items-start gap-3">
                    <Checkbox
                      id="consent-state"
                      checked={formData.consentStateAvailability}
                      onCheckedChange={(c) => updateField("consentStateAvailability", !!c)}
                    />
                    <Label htmlFor="consent-state" className="text-xs text-muted-foreground leading-relaxed font-normal cursor-pointer">
                      <span className="font-medium text-foreground">State availability:</span> I confirm I reside in the state I selected and understand services are only available where Elara Health's affiliated physicians are licensed.
                    </Label>
                  </div>
                  <div className="flex items-start gap-3">
                    <Checkbox
                      id="consent-relationship"
                      checked={formData.consentProviderRelationship}
                      onCheckedChange={(c) => updateField("consentProviderRelationship", !!c)}
                    />
                    <Label htmlFor="consent-relationship" className="text-xs text-muted-foreground leading-relaxed font-normal cursor-pointer">
                      <span className="font-medium text-foreground">Patient–provider relationship:</span> I acknowledge my patient–provider relationship is with the treating physician, not with Elara Health, and that Elara Health does not practice medicine.
                    </Label>
                  </div>
                  <div className="flex items-start gap-3">
                    <Checkbox
                      id="consent-telehealth"
                      checked={formData.consentTelehealth}
                      onCheckedChange={(c) => updateField("consentTelehealth", !!c)}
                    />
                    <Label htmlFor="consent-telehealth" className="text-xs text-muted-foreground leading-relaxed font-normal cursor-pointer">
                      <span className="font-medium text-foreground">Telehealth consent:</span> I consent to receive care via telehealth, understand treatment outcomes are not guaranteed, and have read the{" "}
                      <a href="/medical-disclaimer" target="_blank" className="text-primary hover:underline">Medical Disclaimer</a>,{" "}
                      <a href="/privacy" target="_blank" className="text-primary hover:underline">Privacy Policy</a>, and{" "}
                      <a href="/terms" target="_blank" className="text-primary hover:underline">Terms of Service</a>.
                    </Label>
                  </div>
                </div>
              </div>
            )}

            {/* Navigation */}
            <div className="flex justify-between mt-8 pt-6 border-t border-border">
              {currentStepIndex > 0 ? (
                <Button variant="outline" onClick={back}>Back</Button>
              ) : <div />}

              {currentStep.id !== "results" && (
                <Button onClick={next} disabled={!canProceed()} className="gap-1.5">
                  Continue <ArrowRight size={16} />
                </Button>
              )}

              {currentStep.id === "results" && !isLoggedIn && (
                <Button onClick={handleCreateAccountAndSubmit} disabled={isLoading || !canProceed()} className="gap-1.5">
                  {isLoading ? "Creating account..." : "Create Account & Submit"} {!isLoading && <ArrowRight size={16} />}
                </Button>
              )}

              {currentStep.id === "results" && isLoggedIn && (
                <Button onClick={handleSubmit} disabled={isLoading || !allConsentsChecked} className="gap-1.5">
                  {isLoading ? "Submitting..." : "Continue to Medical Review"} {!isLoading && <Check size={16} />}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default GetStarted;
